import { lazy, Suspense, memo } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { BrandedLoader } from "@/components/ui/BrandedLoader";
// Eager load TooltipProvider - it's small (~2KB) and critical for UI
import { TooltipProvider } from "@/components/ui/tooltip";
// AdminDashboardProvider must be eager - it's a required context for data-fetching hooks
import { AdminDashboardProvider } from "@/contexts/AdminDashboardContext";
// Lazy load admin-only UI components
const ImpersonationBanner = lazy(() => import("@/components/admin-dashboard/ImpersonationBanner").then(m => ({ default: m.ImpersonationBanner })));

// Lazy load toast components to reduce main thread blocking during initial load
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Lazy load Auth to defer supabase-auth bundle for authenticated users
const Auth = lazy(() => import("./pages/Auth"));

// NotFound is small and needed for 404s - keep eager
import NotFound from "./pages/NotFound";

// Lazy load all authenticated routes including Chat (uses heavy markdown bundle)
const Chat = lazy(() => import("./pages/Chat"));

// Lazy load non-critical routes
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Gaps = lazy(() => import("./pages/Gaps"));
const GapDetail = lazy(() => import("./pages/GapDetail"));
const Settings = lazy(() => import("./pages/Settings"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const SuperAdminDashboard = lazy(() => import("./pages/SuperAdminDashboard"));
const AdminOrganizationDirectory = lazy(() => import("./pages/AdminOrganizationDirectory"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));
const NotificationCenter = lazy(() => import("./pages/NotificationCenter"));

// Optimized query client configuration with smart retry logic
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: (failureCount, error: unknown) => {
        // Don't retry on auth/permission errors
        if (error && typeof error === 'object') {
          const err = error as { status?: number; statusCode?: number; code?: string };
          if (err.status === 401 || err.status === 403 || err.statusCode === 401 || err.statusCode === 403) {
            return false;
          }
          // Don't retry on Supabase JWT errors
          if (err.code === 'PGRST301' || err.code === 'PGRST302') {
            return false;
          }
        }
        // Retry other errors up to 2 times
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false, // Don't retry mutations by default
    },
  },
});

// Loading fallback component with branded logo
function LoadingFallback() {
  return <BrandedLoader message="Loading..." size="md" />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingFallback />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingFallback />;
  }

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/chat" replace /> : <Navigate to="/auth" replace />} />
      <Route path="/auth" element={user ? <Navigate to="/chat" replace /> : <Suspense fallback={<LoadingFallback />}><Auth /></Suspense>} />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Chat />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Dashboard />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gaps"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Gaps />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gaps/:id"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <GapDetail />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Settings />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Admin />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-dashboard"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <AdminDashboard />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/super-dashboard"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminDashboard />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/directory"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <AdminOrganizationDirectory />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/accept-invite/:token"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <AcceptInvitation />
          </Suspense>
        }
      />
      <Route
        path="/notification-center"
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <NotificationCenter />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// Memoized App to prevent unnecessary re-renders
const App = memo(() => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
        <AuthProvider>
          <AdminDashboardProvider>
            <TooltipProvider>
              <Suspense fallback={null}>
                <Toaster />
                <Sonner />
              </Suspense>
              <BrowserRouter>
                <ErrorBoundary>
                  <Suspense fallback={null}>
                    <ImpersonationBanner />
                  </Suspense>
                  <ScrollToTop />
                  <AppRoutes />
                </ErrorBoundary>
              </BrowserRouter>
            </TooltipProvider>
          </AdminDashboardProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
));

App.displayName = 'App';

export default App;
